import { Calendar } from '@fullcalendar/core'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import deLocale from '@fullcalendar/core/locales/de'
import enLocale from '@fullcalendar/core/locales/en-gb'

// Declare jQuery as external dependency to prevent conflicts
/* global $, jQuery */

document.addEventListener('DOMContentLoaded', () => {
  const lang = (window.nxt_event_calendar?.locale || 'de').toLowerCase()
  const locale = lang === 'en' ? enLocale : deLocale
  const localizedData = window.nxt_event_calendar ?? {}
  // Access the localized events data
  const eventsData = localizedData.events ?? []

  // Convert WordPress events to FullCalendar format
  const calendarEvents = eventsData.map(event => ({
    id: event.id,
    title: event.title,
    start: event.event_start_datetime || event.event_start_day,
    end: event.event_end_datetime || event.event_end_day,
    url: event.permalink
  }))

  const calendarRoot = document.getElementById('event_calendar')
  let calendar = null

  if (calendarRoot) {
    calendar = new Calendar(calendarRoot, {
      plugins: [dayGridPlugin, timeGridPlugin],
      initialView: 'dayGridMonth',
      locale,
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      },
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      },
      events: calendarEvents,
      displayEventTime: false,
      allDaySlot: false,
      firstDay: 1,
      windowResize: function (view) {
        calendar.updateSize()
      }
    })

    calendar.render()
  }

  const tabs = document.querySelector('.event_list_tabs .tabs')
  if (tabs) {
    const buttons = tabs.querySelectorAll('.greyd_tab')

    buttons.forEach(button => {
      button.addEventListener('click', () => {
        // Update calendar size when tab is clicked
        if (calendar && typeof calendar.updateSize === 'function') {
          calendar.updateSize()
        }
        // Also dispatch resize event for other components
        window.dispatchEvent(new Event('resize'))
      })
    })
  }

  // #region Event Loop Form
  const form = document.querySelector('.event_loop_search_form')
  if (form) {
    const eventCategoryInput = form.querySelector('input[name="event-category"]')
    const eventCountryInput = form.querySelector('input[name="event-country"]')
    const eventLocationInput = form.querySelector('input[name="event-location"]')

    if (eventCategoryInput) {
      eventCategoryInput.addEventListener('change', (e) => {
        form.dispatchEvent(new Event('input'))
      })
    }

    if (eventCountryInput) {
      eventCountryInput.addEventListener('change', (e) => {
        form.dispatchEvent(new Event('input'))
      })
    }

    if (eventLocationInput) {
      eventLocationInput.addEventListener('change', (e) => {
        form.dispatchEvent(new Event('input'))
      })
    }

    // Polling approach to detect property value changes
    let lastCountryValue = eventCountryInput?.value ?? ''
    let lastLocationValue = eventLocationInput?.value ?? ''

    const checkForValueChanges = () => {
      if (eventCountryInput && eventCountryInput.value !== lastCountryValue) {
        lastCountryValue = eventCountryInput.value
        form.dispatchEvent(new Event('input'))
      }
      
      if (eventLocationInput && eventLocationInput.value !== lastLocationValue) {
        lastLocationValue = eventLocationInput.value
        form.dispatchEvent(new Event('input'))
      }
    }

    // Check for changes every 100ms
    const valueChangeInterval = setInterval(checkForValueChanges, 100)

    // Clean up interval when page unloads
    window.addEventListener('beforeunload', () => {
      clearInterval(valueChangeInterval)
    })

    // Handle real-time changes for text inputs, textareas, etc.
    form.addEventListener('input', (e) => {
      e.preventDefault()
      debouncedHandleEventLoopFormChange(e)
    })

    // Handle changes for select dropdowns, checkboxes, radio buttons
    form.addEventListener('change', (e) => {
      e.preventDefault()
      debouncedHandleEventLoopFormChange(e)
    })
  }

  // Debounce utility function
  function debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  // Event Loop Form Handler Functions
  function handleEventLoopFormChange(e) {
    e.preventDefault()
    
    const form = document.querySelector('.event_loop_search_form')
    if (!form) return
    
    const formData = new FormData(form)
    const data = Object.fromEntries(formData)
    
    // Make AJAX call
    makeEventLoopAjaxCall(data)
  }

  // Create debounced version of the form change handler (100ms delay)
  const debouncedHandleEventLoopFormChange = debounce(handleEventLoopFormChange, 100)

  function makeEventLoopAjaxCall(formData) {
    // Prepare AJAX data
    const ajaxData = {
      action: 'nxt_event_loop_search',
      nonce: localizedData.nonce,
      ...formData
    }
    
    // Make the AJAX call using fetch
    fetch(localizedData.ajaxurl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams(ajaxData)
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        updateEventLoopResults(data.data)
      } else {
        console.error('AJAX Error:', data.data)
      }
    })
    .catch(error => {
      console.error('AJAX Request failed:', error)
    })
  }

  function updateEventLoopResults(events) {
    // Capture current calendar state before destroying
    let currentView = 'dayGridMonth'
    let currentDate = new Date()
    
    if (calendar && calendar.view) {
      currentView = calendar.view.type
      currentDate = calendar.getDate()
    }
    
    // Destroy existing calendar
    if (calendar && typeof calendar.destroy === 'function') {
      calendar.destroy()
    }
    
    // Clear the calendar container
    const calendarEl = document.getElementById('event_calendar')
    if (!calendarEl) return
    calendarEl.innerHTML = ''
    
    // Convert events to calendar format (empty array if no events)
    const calendarEvents = events && events.length > 0 ? events.map(event => ({
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      url: event.url
    })) : []
    
    // Create new calendar instance with preserved view and date
    calendar = new Calendar(calendarEl, {
      plugins: [dayGridPlugin, timeGridPlugin],
      initialView: currentView,
      initialDate: currentDate,
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay'
      },
      events: calendarEvents,
      displayEventTime: false,
      allDaySlot: false,
      firstDay: 1,
      eventTimeFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      },
      slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      },
      windowResize: function (view) {
        calendar.updateSize()
      }
    })
    
    // Render the calendar
    calendar.render()
    
    // Force a resize to ensure proper display
    setTimeout(() => {
      if (calendar && typeof calendar.updateSize === 'function') {
        calendar.updateSize()
      }
    }, 100)
  }
  // #endregion
})

